/**
 * Calendar-day helpers shared by the collaboration feeds (MessageTimeline,
 * NotificationCenter, ActivityTimeline). Days are compared in the viewer's
 * time zone, or in `timeZone` when one is given, so "Today" means the same
 * thing as the clock on the reader's screen.
 */
export const DEFAULT_DAY_LABELS = { today: 'Today', yesterday: 'Yesterday' };
function toDate(input) {
    return input instanceof Date ? input : new Date(input);
}
/** `YYYY-MM-DD` of the moment in the given zone: a stable key for grouping. */
export function dayKey(input, timeZone) {
    const date = toDate(input);
    if (Number.isNaN(date.getTime()))
        return 'invalid';
    const parts = new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        ...(timeZone ? { timeZone } : {}),
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '00';
    return `${get('year')}-${get('month')}-${get('day')}`;
}
/** Whether two moments fall on the same calendar day. */
export function sameDay(a, b, timeZone) {
    return dayKey(a, timeZone) === dayKey(b, timeZone);
}
/** The key of the day before `key` (`YYYY-MM-DD`), in plain calendar arithmetic. */
function previousDayKey(key) {
    const [y, m, d] = key.split('-').map(Number);
    const prev = new Date(Date.UTC(y, m - 1, d - 1));
    return prev.toISOString().slice(0, 10);
}
/**
 * "Today", "Yesterday", a weekday and date for this year ("Monday, 3 March"),
 * or a full date for other years.
 */
export function dayLabel(input, options = {}, labels = DEFAULT_DAY_LABELS) {
    const { timeZone, locale, now = Date.now() } = options;
    const date = toDate(input);
    if (Number.isNaN(date.getTime()))
        return '';
    const key = dayKey(date, timeZone);
    const today = dayKey(now, timeZone);
    if (key === today)
        return labels.today;
    if (key === previousDayKey(today))
        return labels.yesterday;
    const sameYear = key.slice(0, 4) === today.slice(0, 4);
    return new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        ...(sameYear ? {} : { year: 'numeric' }),
        ...(timeZone ? { timeZone } : {}),
    }).format(date);
}
/**
 * Splits items into consecutive runs of the same day, keeping their order.
 * Items do not need to be sorted, but a day that appears twice in an unsorted
 * list gets two groups.
 */
export function groupByDay(items, getDate, timeZone) {
    const groups = [];
    for (const item of items) {
        const date = toDate(getDate(item));
        const key = dayKey(date, timeZone);
        const last = groups[groups.length - 1];
        if (last && last.key === key)
            last.items.push(item);
        else
            groups.push({ key, date, items: [item] });
    }
    return groups;
}
//# sourceMappingURL=day-groups.js.map